import React from 'react';
import { Box, IconButton, TextField, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Send } from '@mui/icons-material';

export interface ChatInputFieldProps {
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
}

const ChatInputField = React.forwardRef<
  HTMLTextAreaElement,
  ChatInputFieldProps
>(function ChatInputField(
  {
    value,
    onChange,
    onKeyDown,
    onSend,
    rows = 2,
    placeholder = 'Describe your campaign account ICP profile or campaign objectives you want to achieve…',
    disabled = false
  },
  ref
) {
  const theme = useTheme();
  const hasText = value.trim().length > 0;
  const primary = theme.customColors?.themePrimary ?? theme.palette.primary.main;
  const secondary = theme.customColors?.themeSecondary ?? '#fff';

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#424242',
      alignItems: 'flex-end',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.customColors?.themePrimary
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.customColors?.themePrimary,
        borderWidth: '2px'
      },
      '&.Mui-disabled': {
        opacity: 0.7,
        backgroundColor: '#424242',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255, 255, 255, 0.12)'
        },
        '& .MuiOutlinedInput-input': {
          color: 'rgba(255, 255, 255, 0.78)',
          WebkitTextFillColor: 'rgba(255, 255, 255, 0.78)',
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.58)',
            opacity: 1,
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.58)'
          }
        }
      }
    },
    '& .MuiOutlinedInput-input': {
      color: 'white',
      paddingBottom: '40px',
      '&::-webkit-scrollbar': {
        display: 'none'
      },
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }
  };

  return (
    <Box sx={{ position: 'relative', p: 1 }}>
      <TextField
        multiline
        rows={rows}
        fullWidth
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        disabled={disabled}
        inputRef={ref}
        sx={textFieldSx}
        variant="outlined"
      />
      <Tooltip title="Send message" arrow>
        <span>
          <IconButton
            onClick={onSend}
            disabled={disabled}
            aria-label="Send message"
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              bottom: 16,
              width: 32,
              height: 32,
              backgroundColor:
                hasText && !disabled ? primary : 'transparent',
              color: disabled
                ? alpha(secondary, 0.5)
                : hasText
                  ? secondary
                  : primary,
              '&:hover': {
                backgroundColor:
                  hasText && !disabled ? primary : 'transparent'
              },
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: alpha(secondary, 0.5)
              }
            }}
          >
            <Send fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
});

export default ChatInputField;
